import { SettingCatalogGroups } from "./index";
import type { SettingGroupDefinition, SettingProductArea } from "./types";

/** `not_cataloged` means the named area has no entries AND nothing matched, so
 *  the silence is a coverage gap rather than evidence the feature is missing. */
export type SettingsCatalogCoverageStatus =
  | "covered"
  | "covered_by_other_areas"
  | "not_cataloged"
  | "no_match";

export interface SettingsCatalogCoverage {
  status: SettingsCatalogCoverageStatus;
  /** Areas with at least one catalog entry. Everything else is uncataloged. */
  catalogedProductAreas: readonly SettingProductArea[];
  /** Present whenever status is not `covered`. */
  guidance?: string;
}

/** The contract for an uncovered area: behave exactly as Ask Redo did before
 *  the settings/capability tools existed. */
export const SETTINGS_CATALOG_COVERAGE_FALLBACK_GUIDANCE =
  "The settings catalog does not cover this area yet. That is a coverage gap, NOT evidence the feature is missing or unsupported. Answer exactly as you would if these settings and capability tools were not available to you at all: use your general Redo product knowledge, do not assert an exact setting id, settings path, route, or current value, do not render a navigation card, and do not escalate to a human just because this search came back empty.";

/** Product area is a routing hint, not an ownership boundary — AI settings, for
 *  one, are owned by the support catalog. Matches elsewhere must still surface. */
export const SETTINGS_CATALOG_CROSS_AREA_GUIDANCE =
  "The product area you named has no catalog entries of its own, so these results are owned by other product areas. That is normal and they may well be exactly right — merchants routinely describe a setting in one product's vocabulary when another catalog owns the field. Treat them as real results, but confirm the match fits what the merchant asked before opening one, and describe the setting by where it actually lives rather than by the area you searched.";

let catalogedProductAreas: readonly SettingProductArea[] | undefined;

/** Derived from the static catalog; computed once per process. */
export function getCatalogedProductAreas(): readonly SettingProductArea[] {
  catalogedProductAreas ??= [
    ...new Set(
      SettingCatalogGroups.map(
        (group: SettingGroupDefinition) => group.productArea,
      ),
    ),
  ].sort();
  return catalogedProductAreas;
}

export function isProductAreaCataloged(
  productArea: SettingProductArea,
): boolean {
  return getCatalogedProductAreas().includes(productArea);
}

/** Both catalog searches short-circuit on the same conditions, so the settings
 *  path and the capability path can't drift. */
export function shouldShortCircuitCatalogSearch({
  trimmedQuery,
  sanitizedLimit,
}: {
  trimmedQuery: string;
  sanitizedLimit: number;
}): boolean {
  return !trimmedQuery || sanitizedLimit === 0;
}

export function describeSettingsCatalogCoverage({
  productArea,
  resultCount,
}: {
  productArea?: SettingProductArea;
  resultCount: number;
}): SettingsCatalogCoverage {
  const areas = getCatalogedProductAreas();
  const areaUncataloged =
    productArea !== undefined && !isProductAreaCataloged(productArea);

  // Whether anything matched is the discriminator, not which area owns it:
  // gating on area alone suppresses real results like support-owned AI settings.
  if (resultCount === 0) {
    return {
      status: areaUncataloged ? "not_cataloged" : "no_match",
      catalogedProductAreas: areas,
      guidance: SETTINGS_CATALOG_COVERAGE_FALLBACK_GUIDANCE,
    };
  }

  if (areaUncataloged) {
    return {
      status: "covered_by_other_areas",
      catalogedProductAreas: areas,
      guidance: SETTINGS_CATALOG_CROSS_AREA_GUIDANCE,
    };
  }

  return { status: "covered", catalogedProductAreas: areas };
}
